(() => {
  const SLIDE_MS    = 480;
  const MAX_WAIT_MS = 4000;

  // Wait for the <img> inside `item` to finish loading, then call callback.
  // Because images may use srcset / lazy-loading, we listen for the load event
  // rather than trusting img.complete alone.
  function waitForImage(item, callback) {
    const img = item.querySelector('img');
    if (!img || (img.complete && img.naturalWidth > 0)) { callback(); return; }
    let fired = false;
    const fire = () => { if (!fired) { fired = true; callback(); } };
    img.addEventListener('load',  fire, { once: true });
    img.addEventListener('error', fire, { once: true });
    setTimeout(fire, MAX_WAIT_MS);
  }

  // Helper: build one panel of the animation stage (old or new image).
  // imgBox positions the image on the real .media box (relative to the li panel)
  // so it matches the rendered image's size/position exactly.
  function makePanel(src, translateX, bg, imgBox, objectFit) {
    const panel = document.createElement('div');
    Object.assign(panel.style, {
      position:   'absolute',
      top:        '0',
      left:       '0',
      width:      '100%',
      height:     '100%',
      overflow:   'hidden',
      background: bg || 'transparent',
      transform:  `translateX(${translateX}px)`,
    });
    if (src) {
      const img = new Image();
      img.src = src;
      // L'object-fit du clone suit celui de l'image reelle (contain par defaut,
      // cover pour les affiches "vertical" rognees) -> pas de saut au swap.
      const fit = objectFit || 'contain';
      img.style.cssText = imgBox
        ? `position:absolute;top:${imgBox.top}px;left:${imgBox.left}px;width:${imgBox.width}px;height:${imgBox.height}px;object-fit:${fit};display:block;`
        : `width:100%;height:100%;object-fit:${fit};display:block;`;
      panel.appendChild(img);
    }
    return panel;
  }

  function patchGallery(gallery) {
    if (gallery.__svmGallery) return;
    gallery.__svmGallery = true;

    const originalSAM = gallery.setActiveMedia.bind(gallery);

    gallery.setActiveMedia = function (mediaId, prepend) {
      const viewer  = this.elements?.viewer;
      const target  = viewer?.querySelector(`[data-media-id="${mediaId}"]`)
                   || viewer?.querySelector('[data-media-id]');

      // svmData is pre-filled by patchProductInfo before Dawn mutates the DOM.
      const data    = this.__svmData || null;
      this.__svmData = null;

      // Derive current element from pre-saved data or fall back to live query.
      const current = data?.el || viewer?.querySelector('[data-media-id].is-active');

      // Balayage « sur place » : la variante a change mais pointe vers le meme
      // media. Rien de nouveau a montrer, on joue quand meme le glissement pour
      // confirmer que la selection a pris.
      const inPlace = target === current && this.__svmVariantChange === true;
      this.__svmVariantChange = false;

      if (!target || !current || (target === current && !inPlace)) {
        return originalSAM(mediaId, prepend);
      }

      // --- Use values captured BEFORE Dawn mutates the DOM ---
      // Pre-captured by patchProductInfo (reliable even if current is removed).
      const oldSrc  = data?.src  || (() => {
        const img = current.querySelector('img');
        return img?.currentSrc || img?.src || '';
      })();
      const rect    = data?.rect    || current.getBoundingClientRect();
      const imgBox  = data?.imgBox  || (() => {
        const img = current.querySelector('img');
        const mediaEl = (img && (img.closest('.media') || img.parentElement)) || current;
        const m = mediaEl.getBoundingClientRect();
        return { top: m.top - rect.top, left: m.left - rect.left, width: m.width, height: m.height };
      })();
      const objFit  = data?.objectFit || (() => {
        const img = current.querySelector('img');
        return img ? getComputedStyle(img).objectFit : 'contain';
      })();

      // Garde "même image" : si la cible affiche la MÊME image que le média
      // actif, on ne fait RIEN — ni swap, ni animation — pour éviter un flicker
      // inutile. Elle protège le clic sur une vignette du rail.
      //
      // Exemptée quand le balayage est voulu : un changement de variante vers le
      // même média DOIT s'animer, sinon le client n'a aucun retour visuel. La
      // garde est exemptée, pas supprimée — les deux cas restent distincts.
      const svmBaseSrc = (s) => (s || '').split('?')[0];
      const targetImgEl = target.querySelector('img');
      const newSrc = targetImgEl?.getAttribute('src') || targetImgEl?.currentSrc || '';
      if (!inPlace && oldSrc && newSrc && svmBaseSrc(oldSrc) === svmBaseSrc(newSrc)) {
        return;
      }

      // Let Dawn perform the instant image swap.
      originalSAM(mediaId, prepend);

      // Nothing to animate if we have no old image URL or zero-size rect.
      if (!oldSrc || rect.width < 1 || rect.height < 1) return;

      const W = rect.width;

      const bg = '#F2F2F2';

      // Mobile : la barre de progression (.slider-buttons) est dessinée au bas du
      // slot de visuels. Pour ne PAS la recouvrir (ni la déplacer, ce qui casse
      // son CSS scopé), on raccourcit le stage pour qu'il s'arrête juste au-dessus
      // de la barre. La vraie barre reste en place, intacte et visible.
      let stageH = rect.height;
      if (window.innerWidth < 990) {
        const pbR = this.querySelector('.slider-buttons')?.getBoundingClientRect();
        if (pbR && pbR.top > rect.top && pbR.top < rect.top + rect.height) {
          stageH = pbR.top - rect.top;
        }
      }

      // Create a fixed-position stage that sits exactly over the gallery slot.
      // This covers Dawn's instant swap so the user never sees it flash.
      const stage = document.createElement('div');
      Object.assign(stage.style, {
        position:      'fixed',
        top:           rect.top  + 'px',
        left:          rect.left + 'px',
        width:         W + 'px',
        height:        stageH + 'px',
        overflow:      'hidden',
        zIndex:        window.innerWidth >= 990 ? '49' : '1',
        pointerEvents: 'none',
        background:    bg,
      });

      const oldPanel = makePanel(oldSrc, 0, bg, imgBox, objFit);
      stage.appendChild(oldPanel);
      document.body.appendChild(stage);

      // Le stage (fixed, z-index 49) recouvre le rail de vignettes desktop
      // (.product-thumb-rail, à l'intérieur de la galerie) pendant toute la
      // transition -> il "disparaîtrait" puis réapparaîtrait. On pose un CLONE
      // figé du rail AU-DESSUS du stage le temps du slide ; le vrai rail reste
      // en place dessous. Résultat : le rail semble toujours visible.
      let railClone = null;
      let railTop0 = 0;
      let zEl = null, zParent = null, zNext = null, zPrevStyle = null;
      let zTop0 = 0;
      const railEl = this.querySelector('.product-thumb-rail');
      if (railEl && window.innerWidth >= 990) {
        const rRect = railEl.getBoundingClientRect();
        railTop0 = rRect.top;
        railClone = railEl.cloneNode(true);
        Object.assign(railClone.style, {
          position:      'fixed',
          top:           rRect.top + 'px',
          left:          rRect.left + 'px',
          width:         rRect.width + 'px',
          margin:        '0',
          zIndex:        '49', // appendu après le stage (même z) -> au-dessus
          pointerEvents: 'none',
        });
        document.body.appendChild(railClone);
      }

      // Mobile : l'icône zoom (.product__media-zoom-mobile) est posée en bas à
      // droite du slot de visuels et serait recouverte par le stage. Son CSS
      // n'est PAS scopé à un ancêtre particulier, donc on peut la LIFTER au-dessus
      // du stage (fixed, à sa position exacte) le temps du slide pour qu'elle
      // reste visible, puis on la remet en place au nettoyage.
      const zCandidate = this.querySelector('.product__media-zoom-mobile');
      if (zCandidate && window.innerWidth < 990) {
        zEl = zCandidate;
        zParent = zEl.parentNode;
        zNext = zEl.nextSibling;
        zPrevStyle = zEl.getAttribute('style');
        const zRect = zEl.getBoundingClientRect();
        zTop0 = zRect.top;
        Object.assign(zEl.style, {
          position: 'fixed',
          top:      zRect.top + 'px',
          left:     zRect.left + 'px',
          width:    zRect.width + 'px',
          height:   zRect.height + 'px',
          margin:   '0',
          bottom:   'auto',
          right:    'auto',
          zIndex:   '50', // au-dessus du stage
        });
        document.body.appendChild(zEl);
      }

      // Track the gallery's scroll movement so the fixed-position stage follows it.
      // The gallery is not sticky, so its viewport position shifts by -scrollDelta
      // as the user scrolls. Capture the baseline scroll position at stage creation.
      const stageScrollY0 = window.scrollY;
      const onScroll = () => {
        const dy = window.scrollY - stageScrollY0;
        stage.style.top = (rect.top - dy) + 'px';
        if (railClone) railClone.style.top = (railTop0 - dy) + 'px';
        if (zEl) zEl.style.top = (zTop0 - dy) + 'px';
      };
      window.addEventListener('scroll', onScroll, { passive: true });

      // Once the new image has loaded, build the new panel and slide.
      waitForImage(target, () => {
        const newImg = target.querySelector('img');
        const newSrc = newImg?.currentSrc || newImg?.src || '';
        const newPanel = makePanel(newSrc, W, bg, imgBox, objFit);
        stage.appendChild(newPanel);

        // Commit initial positions (reflow), then start CSS transitions.
        void stage.offsetWidth;

        const t = `transform ${SLIDE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`;
        oldPanel.style.transition = t;
        newPanel.style.transition = t;
        oldPanel.style.transform  = `translateX(${-W}px)`;
        newPanel.style.transform  = 'translateX(0)';

        setTimeout(() => {
          stage.remove();
          if (railClone) railClone.remove();
          // Remet l'icône zoom exactement à sa place (style + position DOM).
          if (zEl && zParent) {
            if (zPrevStyle === null) zEl.removeAttribute('style');
            else zEl.setAttribute('style', zPrevStyle);
            zParent.insertBefore(zEl, zNext);
          }
          window.removeEventListener('scroll', onScroll);
        }, SLIDE_MS + 50);
      });
    };
  }

  function patchProductInfo(pi) {
    if (pi.__svmPI) return;
    pi.__svmPI = true;

    const origUM = pi.updateMedia.bind(pi);

    pi.updateMedia = function (html, variantFeaturedMediaId) {
      const gallery = this.querySelector('media-gallery');
      const viewer  = gallery?.elements?.viewer;
      const active  = viewer?.querySelector('[data-media-id].is-active');

      // Pre-capture everything from the live DOM NOW, before origUM may
      // remove or reorder the active element.
      if (gallery && active) {
        const img = active.querySelector('img');
        const mediaEl = (img && (img.closest('.media') || img.parentElement)) || active;
        const liRect = active.getBoundingClientRect();
        const m = mediaEl.getBoundingClientRect();
        gallery.__svmData = {
          el:      active,
          src:     img?.currentSrc || img?.src || '',
          // Stage = full <li> box: the slide spans the full width and covers
          // Dawn's instant swap (so the new image isn't revealed early).
          rect:    liRect,
          // Image positioned on the real .media box so its size/position match
          // the rendered image exactly (no proportion/offset drift).
          imgBox:  { top: m.top - liRect.top, left: m.left - liRect.left, width: m.width, height: m.height },
          // object-fit reel de l'image (contain / cover selon le format produit).
          objectFit: img ? getComputedStyle(img).objectFit : 'contain',
        };
      }

      // La variante change : on l'annonce a la galerie. Sert au cas ou la
      // nouvelle variante pointe vers le MEME media que l'actuelle — l'A0
      // reutilise le visuel du 70x100, idem pour les carres. Sans ce drapeau,
      // setActiveMedia sort en amont sur target === current et rien ne bouge.
      //
      // La lecture est sure : product-info.js appelle setActiveMedia de facon
      // synchrone depuis updateMedia, le drapeau est donc consomme pendant
      // origUM.
      if (gallery) gallery.__svmVariantChange = true;

      const out = origUM(html, variantFeaturedMediaId);

      // Filet : si Dawn sort avant d'appeler setActiveMedia, le drapeau ne doit
      // pas survivre jusqu'au prochain clic sur une vignette.
      if (gallery) gallery.__svmVariantChange = false;

      return out;
    };
  }

  // ---------------------------------------------------------------------------
  // MoBe 22/10/2026 (recette) : le visuel de la variante visee part des le choix.
  // Le visuel de chaque variante n'est pas dans la page : il arrivait avec le
  // rendu de section et ne partait qu'apres lui. Rendu (~250 ms), puis
  // redimensionnement par le CDN a la premiere demande de chaque largeur
  // (500 a 1 400 ms) et telechargement s'additionnaient avant le glissement.
  // Desormais il part au choix de l'option, en parallele du rendu, et meme des
  // le survol de l'option a la souris (80 ms) ou l'appui du doigt.
  // Memes srcset et sizes que la galerie : le navigateur choisit le meme
  // fichier, que la galerie retrouve ensuite en memoire (une seule requete).
  // Facultatif par nature : toute erreur est ignoree, le parcours reste celui
  // d'avant.
  // ---------------------------------------------------------------------------
  const svmPreloaded = new Map();
  const svmVariantsCache = new WeakMap();
  const SVM_MAX_HOVER_PRELOADS = 8; // survols sans clic : plafond par page
  let svmHoverPreloads = 0;
  let svmHoverTimer = null;

  function svmVariants(vs) {
    if (svmVariantsCache.has(vs)) return svmVariantsCache.get(vs);
    const json = vs.querySelector('script[type="application/json"]:not([data-selected-variant])');
    const list = json ? JSON.parse(json.textContent) : [];
    svmVariantsCache.set(vs, list);
    return list;
  }

  function svmPreloadVariantMedia(pi, variant) {
    const media = variant && variant.featured_media;
    const preview = media && media.preview_image;
    if (!preview || !preview.src || svmPreloaded.has(media.id)) return false;
    const ref =
      pi.querySelector('media-gallery li[data-media-id].is-active img[srcset]') ||
      pi.querySelector('media-gallery li[data-media-id] img[srcset]');
    if (!ref) return false;
    const widths = ref
      .getAttribute('srcset')
      .split(',')
      .map((c) => parseInt(c.trim().split(/\s+/)[1], 10))
      .filter((w) => w > 0 && (!preview.width || w <= preview.width));
    if (!widths.length) return false;
    const sep = preview.src.indexOf('?') === -1 ? '?' : '&';
    const img = new Image();
    img.decoding = 'async';
    img.sizes = ref.getAttribute('sizes') || '100vw';
    img.srcset = widths.map((w) => preview.src + sep + 'width=' + w + ' ' + w + 'w').join(', ');
    svmPreloaded.set(media.id, img);
    return true;
  }

  // Choix fait : les selects de Dawn portent deja la combinaison complete
  // (custom-variant-ui.js les ecrit tous avant son dispatch unique).
  function svmPreloadFromSelects(pi, vs) {
    const values = [...vs.querySelectorAll('.product-form__input')].map((c) => {
      const select = c.querySelector('select');
      if (select) return select.value;
      const radio = c.querySelector('input[type="radio"]:checked');
      return radio ? radio.value : null;
    });
    const variant = svmVariants(vs).find((v) => (v.options || []).every((o, i) => o === values[i]));
    svmPreloadVariantMedia(pi, variant);
  }

  // Option survolee ou touchee dans les listes Size / Frame : cette option,
  // plus la valeur courante de l'autre liste.
  function svmPreloadFromOption(option) {
    const dropdown = option.closest('.custom-dropdown');
    const wrapper = option.closest('[data-custom-variants]');
    const pi = option.closest('product-info');
    const vs = pi && pi.querySelector('variant-selects');
    if (!dropdown || !wrapper || !vs || option.classList.contains('is-disabled')) return false;
    const sizeEl = wrapper.querySelector('[data-cv-size]');
    const frameEl = wrapper.querySelector('[data-cv-frame]');
    const size = dropdown.dataset.dropdown === 'size' ? option.dataset.value : sizeEl && sizeEl.value;
    const frame = dropdown.dataset.dropdown === 'frame' ? option.dataset.value : frameEl && frameEl.value;
    if (!size || !frame) return false;
    const matches = svmVariants(vs).filter(
      (v) => (v.options || []).indexOf(size) !== -1 && (v.options || []).indexOf(frame) !== -1
    );
    return svmPreloadVariantMedia(pi, matches.filter((v) => v.available)[0] || matches[0]);
  }

  function svmBindPreload(pi) {
    if (pi.__svmPreload) return;
    pi.__svmPreload = true;
    pi.addEventListener(
      'change',
      (e) => {
        const vs = e.target.closest && e.target.closest('variant-selects');
        if (!vs) return;
        try {
          svmPreloadFromSelects(pi, vs);
        } catch (err) {
          /* prechargement facultatif */
        }
      },
      true
    );
  }

  if (!window.__svmPreloadBound) {
    window.__svmPreloadBound = true;
    document.addEventListener('pointerover', (e) => {
      if (e.pointerType !== 'mouse') return;
      const option = e.target.closest && e.target.closest('.custom-dropdown__option');
      clearTimeout(svmHoverTimer);
      if (!option || svmHoverPreloads >= SVM_MAX_HOVER_PRELOADS) return;
      svmHoverTimer = setTimeout(() => {
        try {
          if (svmPreloadFromOption(option)) svmHoverPreloads++;
        } catch (err) {
          /* prechargement facultatif */
        }
      }, 80);
    });
    document.addEventListener(
      'pointerdown',
      (e) => {
        const option = e.target.closest && e.target.closest('.custom-dropdown__option');
        if (!option) return;
        try {
          svmPreloadFromOption(option);
        } catch (err) {
          /* prechargement facultatif */
        }
      },
      true
    );
  }

  function init() {
    document.querySelectorAll('media-gallery').forEach(patchGallery);
    document.querySelectorAll('product-info').forEach(patchProductInfo);
    document.querySelectorAll('product-info').forEach(svmBindPreload);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
